/**
 * "Offline speichern": downloads what a diver needs without signal and stores it in the Cache API
 * (`CACHES.saved`, never evicted automatically) plus an index entry. Keys are the exact URLs the app
 * itself requests, so the service worker finds them. Cancel and failures leave nothing half-saved.
 */
import { SpotDetail, decodeTerrain, routes } from "@divespots/contracts";
import { terrainQuery } from "../api/client.ts";
import { buildUrl } from "../api/live-transport.ts";
import { CACHES } from "../pwa/cache-rules.ts";
import { aerialUrl, initialRadius } from "../spot/three/geo.ts";
import { estimateSaveBytes, tileUrls } from "./tiles.ts";
import type { SavedSpot, SavedStore } from "./store.ts";

export type SaveErrorKind = "quota" | "network" | "failed";

export class SaveError extends Error {
  constructor(
    readonly kind: SaveErrorKind,
    message: string,
  ) {
    super(message);
    this.name = "SaveError";
  }
}

export interface SaveProgress {
  done: number;
  total: number;
  bytes: number;
}

export interface SaveDeps {
  store: SavedStore;
  caches?: CacheStorage;
  fetchFn?: typeof fetch;
  /** Called once, before the first write; the page uses it for `navigator.storage.persist()`. */
  beforeWrite?: () => Promise<void> | void;
}

/** The detail as an anonymous visitor sees it: published objects only, `objectCount` to match. */
export function publicSpotDetail(raw: unknown): unknown {
  const spot = raw as { objects?: { status?: string }[]; objectCount?: number };
  if (!spot || !Array.isArray(spot.objects)) return raw;
  const objects = spot.objects.filter((o) => o.status === "published");
  return { ...spot, objects, objectCount: objects.length };
}

export const detailUrl = (slug: string): string => buildUrl(routes.getSpot.path, { slug }, {});

/** The radius the 3D view opens with (and so the terrain tile that gets saved). */
export const terrainRadiusFor = (spot: SpotDetail): number | null =>
  spot.hasBathy
    ? initialRadius(
        spot.location,
        spot.objects.map((o) => o.location),
      )
    : null;

export const terrainUrl = (spot: SpotDetail, radius: number): string =>
  buildUrl(
    routes.terrain.path,
    {},
    terrainQuery({ lat: spot.location.lat, lon: spot.location.lon, radius }),
  );

/** What a save would download, for the "ca. 18 MB" shown before anything starts. */
export const estimateFor = (spot: SpotDetail) =>
  estimateSaveBytes(spot.location, terrainRadiusFor(spot));

const isQuota = (e: unknown): boolean =>
  e instanceof DOMException && (e.name === "QuotaExceededError" || e.code === 22);

const isAbort = (e: unknown): boolean => e instanceof DOMException && e.name === "AbortError";

const CONCURRENCY = 6;

export async function saveSpot(
  spot: SpotDetail,
  deps: SaveDeps,
  signal: AbortSignal,
  onProgress: (p: SaveProgress) => void = () => undefined,
): Promise<SavedSpot> {
  const doFetch = deps.fetchFn ?? ((i, init) => fetch(i, init));
  const cacheStorage = deps.caches ?? caches;
  const cache = await cacheStorage.open(CACHES.saved);
  const written: string[] = []; // keys put by this run, undone on cancel / failure
  const keys: string[] = [];
  let bytes = 0;
  let done = 0;
  let total = 2; // detail + (terrain | nothing); grows below
  const report = () => onProgress({ done, total, bytes });

  const existing = new Set(
    (await deps.store.list()).flatMap((s) => (s.slug === spot.slug ? [] : s.keys)),
  );

  const grab = async (url: string, optional: boolean) => {
    signal.throwIfAborted();
    if (existing.has(url) && (await cache.match(url))) {
      keys.push(url);
      done++;
      report();
      return;
    }
    let res: Response;
    try {
      res = await doFetch(url, {
        mode: "cors",
        // Always the PUBLIC view: a moderator's session must never put drafts into a cache that
        // outlives the logout.
        credentials: "omit",
        signal,
      });
    } catch (e) {
      if (isAbort(e)) throw e;
      throw new SaveError("network", "offline");
    }
    if (!res.ok) {
      // some overlay tiles do not exist (no relief outside lakes); the spot itself must
      if (optional) {
        done++;
        report();
        return;
      }
      throw new SaveError("failed", `${url}: HTTP ${res.status}`);
    }
    const raw = await res.blob();
    let body: Blob | Uint8Array = raw;
    let size = raw.size;
    if (url === detailUrl(spot.slug)) {
      // defence in depth: whatever the server answered, only published objects are stored
      body = new TextEncoder().encode(
        JSON.stringify(publicSpotDetail(JSON.parse(await raw.text()))),
      );
      size = body.byteLength;
    }
    try {
      await cache.put(
        url,
        new Response(body as BodyInit, {
          headers: { "content-type": res.headers.get("content-type") ?? "" },
        }),
      );
    } catch (e) {
      if (isQuota(e)) throw new SaveError("quota", "storage full");
      throw e;
    }
    written.push(url);
    keys.push(url);
    bytes += size;
    done++;
    report();
  };

  const undo = async () => {
    const keep = new Set(
      (await deps.store.list()).flatMap((s) => (s.slug === spot.slug ? [] : s.keys)),
    );
    await Promise.all(written.filter((k) => !keep.has(k)).map((k) => cache.delete(k)));
  };

  try {
    await deps.beforeWrite?.();
    report();
    await grab(detailUrl(spot.slug), false);
    SpotDetail.parse(await (await cache.match(detailUrl(spot.slug)))?.clone().json());

    const radius = terrainRadiusFor(spot);
    if (radius !== null) {
      const tUrl = terrainUrl(spot, radius);
      await grab(tUrl, false);
      const buf = await (await cache.match(tUrl))?.arrayBuffer();
      if (!buf) throw new SaveError("failed", "terrain missing after save");
      const { meta } = decodeTerrain(buf);
      total += 1;
      await grab(aerialUrl(meta), true); // the photo is a nicety: the scene falls back to plain land
    }

    const tiles = tileUrls(spot.location);
    total += tiles.length;
    report();
    let next = 0;
    let failed = false;
    // every worker settles before anything is undone: none may write after the cleanup
    const results = await Promise.allSettled(
      Array.from({ length: CONCURRENCY }, async () => {
        while (next < tiles.length && !failed) {
          const url = tiles[next++] as string;
          try {
            await grab(url, true);
          } catch (e) {
            failed = true;
            throw e;
          }
        }
      }),
    );
    const rejected = results.find((r): r is PromiseRejectedResult => r.status === "rejected");
    if (rejected) throw rejected.reason;

    // The service worker also keeps a runtime copy of what the page fetched: drop it, one copy is enough.
    for (const name of [CACHES.tiles, CACHES.wms]) {
      const c = await cacheStorage.open(name);
      await Promise.all(written.map((k) => c.delete(k)));
    }

    const record: SavedSpot = {
      slug: spot.slug,
      name: spot.name,
      savedAt: Date.now(),
      bytes,
      radiusM: radius,
      keys,
    };
    await deps.store.put(record);
    return record;
  } catch (e) {
    await undo().catch(() => undefined);
    if (isQuota(e)) throw new SaveError("quota", "storage full");
    throw e;
  }
}

/** Removes a saved spot and every cached file no other saved spot still needs. */
export async function removeSpot(
  slug: string,
  deps: Pick<SaveDeps, "store" | "caches">,
): Promise<void> {
  const all = await deps.store.list();
  const target = all.find((s) => s.slug === slug);
  if (!target) return;
  const keep = new Set(all.flatMap((s) => (s.slug === slug ? [] : s.keys)));
  const cache = await (deps.caches ?? caches).open(CACHES.saved);
  await Promise.all(target.keys.filter((k) => !keep.has(k)).map((k) => cache.delete(k)));
  await deps.store.delete(slug);
}
